import type { MatchState, PlayerState } from './state';
import type { PlayerInput } from '../types';
import type { SimConfig } from '../config/simConfig';
import { DT } from '../config/simConfig';
import { clampMag, angleDiff, slewAngle } from '../math/vec';
import { isInputIgnored } from './actions';
import { inOwnBox } from './gk';
import { BTN } from './input';

/**
 * Step 3 of §5.1 — the player movement model (§5.2). Acceleration-limited approach to the desired
 * velocity, a heading-based turn-rate clamp (vs the PREVIOUS velocity heading — never facing), then
 * position integration and a facing slew. Mutates `state.players`.
 */
/** D132: the OPPONENT carrier, or null (loose ball, or one of ours has it). The BLOCK lock-on target. */
function opponentCarrier(state: MatchState, p: PlayerState): PlayerState | null {
  if (state.ball.possessor === null) return null;
  const c = state.players[state.ball.possessor];
  return c && c.team !== p.team && !c.sentOff ? c : null;
}

export function integratePlayers(state: MatchState, inputs: readonly PlayerInput[], cfg: SimConfig): void {
  const mv = cfg.movement;
  const conditionSpeedMul = cfg.conditions[state.conditions].conditionSpeedMul;
  const maxAccel = mv.MAX_ACCEL * DT;
  const maxTurn = mv.TURN_RATE_MAX * DT;
  const faceSlew = mv.FACE_SLEW * DT;

  for (const p of state.players) {
    if (p.sentOff) continue;

    // D113 (HB-D) — VERTICAL motion. `z`/`vz` have been in the state, the clone, the hash, the keyframe
    // codec and the snapshot since M1, and nothing has ever written them. HEADER_JUMP is the only thing
    // that sets `vz`, and the guard means a grounded player's arithmetic is not merely equivalent but
    // NEVER RUNS — so every existing trajectory is bit-identical (the goldens assert it). Placed before
    // the ballistic early-continues so a jump is integrated from any action state.
    if (p.z > 0 || p.vz !== 0) {
      p.vz -= cfg.ball.GRAVITY * DT;
      p.z += p.vz * DT;
      if (p.z <= 0) {
        p.z = 0;
        p.vz = 0;
      }
    }

    // D155 — the GOAL_CELEBRATION ceremony owns movement outright, so integrate the velocity it wrote.
    // `applyCelebration` sets the orbit/trudge velocities at sub-step 8; running the full model over them
    // here with NULL_INPUT gives a steering vector of 0, so `speedTarget` is 0 and the accel clamp
    // subtracts MAX_ACCEL·DT (0.6 m/s) from the ceremony's velocity BEFORE integrating position. Every
    // mobbing player therefore travelled at ORBIT_SPEED − 0.6 while the velocity in the snapshot — and so
    // the animation's run blend — was ORBIT_SPEED, a constant chosen to match RUN_REFERENCE_SPEED exactly
    // so the run clip plays at its authored cadence. Same shape as the two ballistic branches below:
    // an action that supplies its own velocity is integrated, not re-derived. Facing is untouched here
    // because applyCelebration rewrites it at step 8 regardless.
    if (state.phase === 'GOAL_CELEBRATION') {
      p.pos.x += p.vel.x * DT;
      p.pos.y += p.vel.y * DT;
      continue;
    }

    // slide tackle: ballistic motion decelerating at SLIDE_DECEL·MUL (§5.4), not the movement model
    if (p.action === 'TACKLE_SLIDE') {
      const speed = Math.hypot(p.vel.x, p.vel.y);
      if (speed > 1e-9) {
        const decel = cfg.actions.slide.DECEL * cfg.conditions[state.conditions].SLIDE_DECEL_MUL * DT;
        const k = Math.max(0, speed - decel) / speed;
        p.vel.x *= k;
        p.vel.y *= k;
      }
      p.pos.x += p.vel.x * DT;
      p.pos.y += p.vel.y * DT;
      continue;
    }

    // GK dive: ballistic lateral motion (input ignored), decelerating like a slide but gentler
    if (p.action === 'GK_DIVE') {
      const speed = Math.hypot(p.vel.x, p.vel.y);
      if (speed > 1e-9) {
        const decel = cfg.actions.slide.DECEL * 0.5 * DT;
        const k = Math.max(0, speed - decel) / speed;
        p.vel.x *= k;
        p.vel.y *= k;
      }
      p.pos.x += p.vel.x * DT;
      p.pos.y += p.vel.y * DT;
      continue;
    }

    const input = inputs[p.id];

    // steering vector (zeroed for input-ignored actions §5.4; clamped to unit magnitude)
    let mvx = 0;
    let mvy = 0;
    if (input && !isInputIgnored(p.action)) {
      mvx = input.moveX;
      mvy = input.moveY;
      const m = Math.hypot(mvx, mvy);
      if (m > 1) {
        mvx /= m;
        mvy /= m;
      }
    }

    const staminaFactor = mv.STAMINA_FACTOR_MIN + (1 - mv.STAMINA_FACTOR_MIN) * p.stamina;
    // D40: the ball is a handbrake — sprint with it glued is SPRINT_CARRY_MUL (a sprinting defender
    // closes). D82: CONTAIN is now the BLOCK stance (set in applyActions); a braced blocker is heavily
    // slowed (block.MOVE_MUL, no sprint) — the old quick-strafe CONTAIN_SPEED/ACCEL_MULs are retired.
    const carrying = state.ball.possessor === p.id;
    const blocking = p.action === 'BLOCK';
    const sprintMul = !blocking && p.sprinting ? (carrying ? mv.SPRINT_CARRY_MUL : mv.SPRINT_MUL) : 1;
    const actionSpeedMul = blocking
      ? cfg.actions.block.MOVE_MUL
      : p.action === 'GK_HOLD' && p.actionTicks < cfg.actions.gk.SETTLE_TICKS
        ? 0 // D35 catch-settle: the keeper is rooted while gathering the ball
        : (mv.ACTION_SPEED_MUL[p.action] ?? 1);
    const speedTarget = mv.MAX_SPEED * sprintMul * staminaFactor * conditionSpeedMul * actionSpeedMul;

    const accel = clampMag({ x: mvx * speedTarget - p.vel.x, y: mvy * speedTarget - p.vel.y }, maxAccel);
    let vpx = p.vel.x + accel.x;
    let vpy = p.vel.y + accel.y;

    // heading-based turn-rate clamp (§5.2): only when the PREVIOUS speed ≥ TURN_EXEMPT_SPEED
    const prevSpeed = Math.hypot(p.vel.x, p.vel.y);
    const newSpeed = Math.hypot(vpx, vpy);
    if (prevSpeed >= mv.TURN_EXEMPT_SPEED && newSpeed > 0) {
      const prevHeading = Math.atan2(p.vel.y, p.vel.x);
      const newHeading = Math.atan2(vpy, vpx);
      const dTheta = angleDiff(newHeading, prevHeading);
      if (Math.abs(dTheta) > maxTurn) {
        const clamped = prevHeading + Math.sign(dTheta) * maxTurn;
        vpx = Math.cos(clamped) * newSpeed;
        vpy = Math.sin(clamped) * newSpeed;
      }
    }

    p.vel.x = vpx;
    p.vel.y = vpy;
    p.pos.x += vpx * DT;
    p.pos.y += vpy * DT;

    // facing slews toward the velocity heading, or aimYaw when shielding/charging (§5.2)
    const shieldingOrCharging =
      p.action === 'SHIELD' || p.charge !== null || (input ? (input.buttons & BTN.SHIELD) !== 0 : false);
    // D132 (§7.2's spec for BLOCK: "widened stance, player LOCKS ONTO ball-possessing attacker") — a
    // braced defender turns to face the man he is blocking. Only an OPPONENT CARRIER: a loose ball or a
    // teammate on it leaves the ordinary velocity facing, so the stance never quietly steers you. Ranked
    // below the shield/charge branch, which is that player's OWN aim and must keep winning.
    const blockTarget = !shieldingOrCharging && p.action === 'BLOCK' ? opponentCarrier(state, p) : null;
    // D133 (§4, ✅ user: "the keeper never turns his back on the ball") — a keeper in his OWN BOX slews
    // to his aimYaw, strafe-style, instead of to his velocity heading. Today facing follows velocity, so
    // a shuffling keeper faces sideways and an idle one faces wherever he last moved, even though
    // `keeper.ts` already aims him at the ball. Input-only, so the sim never needs to know who is human:
    // a BOT keeper aims at the ball and therefore faces it always; a HUMAN keeper faces where he points
    // the mouse, which IS his control and is exactly the "unless a human controls him" carve-out.
    // §5.2 conflict note: the facing law gains a keeper clause — logged with the D133 entry.
    const keeperFacing = !shieldingOrCharging && p.isGk && input !== undefined && inOwnBox(state, p);
    let targetFacing = p.facing;
    if (shieldingOrCharging && input) targetFacing = input.aimYaw;
    else if (keeperFacing && input) targetFacing = input.aimYaw;
    else if (blockTarget) targetFacing = Math.atan2(blockTarget.pos.y - p.pos.y, blockTarget.pos.x - p.pos.x);
    else if (newSpeed > 1e-4) targetFacing = Math.atan2(vpy, vpx);
    p.facing = slewAngle(p.facing, targetFacing, faceSlew);
  }
}
